import React, { TextareaHTMLAttributes, useEffect, useRef } from "react";
import { PencilSquareIcon } from "@heroicons/react/24/solid";
import Button from "./Button";

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  required?: boolean;
  convertPlaceholder?: boolean;
  isInteger?: boolean;
}

export default function Textarea({
  label,
  required,
  className,
  convertPlaceholder = true,
  isInteger = false,
  ...props
}: TextareaProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, [props.value]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (isInteger && props.onChange) {
      // Remove all non-integer characters and keep only digits
      const cleanedValue = e.target.value.replace(/[^0-9]/g, "");
      // Create a new event with the cleaned value
      const cleanedEvent = {
        ...e,
        target: {
          ...e.target,
          value: cleanedValue,
        },
      };
      props.onChange(cleanedEvent);
    } else if (props.onChange) {
      props.onChange(e);
    }
  };

  const convertPlaceholderFunc = () => {
    if (convertPlaceholder && props.onChange) {
      if (isInteger) {
        const cleanedValue = props.placeholder.replace(/[^0-9]/g, "");
        props.onChange({
          target: { value: cleanedValue },
        } as React.ChangeEvent<HTMLTextAreaElement>);
        return;
      }
      props.onChange({
        target: { value: props.placeholder },
      } as React.ChangeEvent<HTMLTextAreaElement>);
    }
  };

  return (
    <div className={`space-y-2 ${className ? className : ""}`}>
      {label && (
        <label htmlFor={props.id} className="font-bold">
          {label}
          {required && <span className="text-red-500"> *</span>}
        </label>
      )}
      <div className="relative w-full">
        <textarea
          ref={textareaRef}
          className="w-full px-4 py-2 pr-10 border border-[#0F172A] rounded-md shadow-lg focus:outline-none focus:border-[#00A3E0] disabled:cursor-not-allowed"
          rows={2}
          {...props}
          onChange={handleChange}
        />
        {convertPlaceholder &&
          props.value === "" &&
          props.placeholder &&
          props.placeholder.trim() !== "" && (
            <Button.Circle
              variant="custom"
              className="absolute top-2 right-2 p-1 text-gray-500 bg-white shadow-md hover:bg-gray-100 focus:outline-none"
              onClick={convertPlaceholderFunc}
            >
              <PencilSquareIcon className="w-5 h-5 text-electrum-200" />
            </Button.Circle>
          )}
      </div>
    </div>
  );
}
